import { WASH_CREDIT_STATUS_LABELS, WASH_CREDIT_STATUSES, type WashCredit } from "@station/contracts";
import { Alert, Card, DataTable, type DataTableColumn, EmptyState, FormField, Select } from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Timer } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { type CreditFilters, creditsQuery, useWashCredits } from "../../../../features/wash/queries.js";
import {
  CreditStatusBadge,
  washSearchShape,
  WashStationPage,
  type WashStationContext,
} from "../../../../features/wash/shared.js";
import { creditLabel, formatDuration } from "../../../../features/wash/wash-values.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatDateTime } from "../../../../lib/format.js";

const searchSchema = z.object({
  ...washSearchShape,
  status: z.enum(WASH_CREDIT_STATUSES).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/yikama/krediler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CreditsRoute,
});

/**
 * Self-servis süre kredileri (Prompt 18 web, ADR 0018 §10). Kod bearer'dır: listede ve ayrıntıda kod / kod özeti HİÇ
 * yoktur; kalıcı kart numarası maskelidir (son 4 hane). Bakiye saniyedir ve eksiye düşebilir (çevrimdışı çift kullanım
 * reddedilmez — uyarı olayı). Düzeltme ve kilit ayrıntı sayfasındadır (`wash.manage`).
 */
function CreditsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <WashStationPage title={t("menu.washCredits")} description={t("wash.credits.description")}>
      {(context) => <CreditsBody context={context} />}
    </WashStationPage>
  );
}

function CreditsBody({ context }: { context: WashStationContext }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const filters: CreditFilters = { stationId: context.station.id, status: search.status };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useWashCredits(filters, pages.cursor);

  const columns = useMemo<DataTableColumn<WashCredit>[]>(
    () => [
      {
        id: "card",
        header: t("wash.credits.card"),
        hideable: false,
        exportValue: (row) => creditLabel(row, t("wash.credits.codeCredit")),
        cell: (row) => (
          <Link
            to="/yikama/krediler/$id"
            params={{ id: row.id }}
            search={{ station: context.station.id }}
            className="font-mono underline underline-offset-4"
          >
            {creditLabel(row, t("wash.credits.codeCredit"))}
          </Link>
        ),
      },
      {
        id: "balance",
        header: t("wash.credits.balance"),
        align: "right",
        exportValue: (row) => Number(row.balanceSeconds),
        cell: (row) => (
          <span className={`tabular ${row.balanceSeconds.startsWith("-") ? "text-destructive" : ""}`}>
            {formatDuration(row.balanceSeconds)}
          </span>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => WASH_CREDIT_STATUS_LABELS[row.status],
        cell: (row) => <CreditStatusBadge status={row.status} />,
      },
      {
        id: "expiresAt",
        header: t("wash.credits.expiresAt"),
        exportValue: (row) => row.expiresAt,
        cell: (row) => <span className="tabular">{formatDateTime(row.expiresAt)}</span>,
      },
      {
        id: "createdAt",
        header: t("wash.credits.createdAt"),
        exportValue: (row) => row.createdAt,
        cell: (row) => <span className="tabular">{formatDateTime(row.createdAt)}</span>,
      },
      {
        id: "updatedAt",
        header: t("wash.credits.updatedAt"),
        defaultVisible: false,
        exportValue: (row) => row.updatedAt,
        cell: (row) => <span className="tabular">{formatDateTime(row.updatedAt)}</span>,
      },
    ],
    [t, context.station.id],
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField label={t("common.status")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={search.status ?? ""}
              onChange={(event) =>
                void navigate({
                  to: ".",
                  search: ((previous: Record<string, unknown>) =>
                    compactSearch({ ...previous, status: event.target.value || undefined })) as never,
                  replace: true,
                })
              }
            >
              <option value="">{t("common.all")}</option>
              {WASH_CREDIT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {WASH_CREDIT_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <p className="max-w-xl text-xs text-muted-foreground">{t("wash.credits.codeNotice")}</p>
      </Card>
      <DataTable
        caption={t("wash.credits.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Timer />}
            title={t("wash.credits.emptyTitle")}
            description={t("wash.credits.emptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<WashCredit>(
            async (cursor) =>
              (
                await api.GET("/wash/credits", {
                  params: { query: creditsQuery(filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "yikama-kredileri",
            sheetName: t("menu.washCredits"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}
